import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Camera, Loader2, Sparkles, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";

const PRESETS = [
  { label: "+3g", days: 3 },
  { label: "+7g", days: 7 },
  { label: "+15g", days: 15 },
  { label: "+30g", days: 30 },
  { label: "+6m", days: 180 },
  { label: "+1a", days: 365 },
];

function addDays(days) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function ExpiryDateInput({ value, onChange, category, name, testid, disabled = false }) {
  const [scanning, setScanning] = useState(false);
  const [hint, setHint] = useState(null);
  const camRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!category) {
      setHint(null);
      return;
    }
    let cancelled = false;
    api
      .getShelfLifeHints(category, name)
      .then((h) => {
        if (!cancelled) setHint(h);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [category, name]);

  const handleScan = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/jpg"].includes(file.type)) {
      toast.error("Formato non supportato", { description: "Usa JPEG, PNG o WEBP" });
      return;
    }
    setScanning(true);
    try {
      const res = await api.scanExpiry(file);
      if (res.date) {
        onChange(res.date);
        toast.success(`Scadenza letta: ${res.date}`, {
          description: res.raw_text ? `Testo originale: "${res.raw_text}"` : undefined,
        });
      } else {
        toast.error("Data non leggibile", {
          description: "Riprova con una foto più nitida o inquadrata meglio",
        });
      }
    } catch (err) {
      toast.error("Errore scansione", { description: err?.response?.data?.detail || err.message });
    } finally {
      setScanning(false);
      if (camRef.current) camRef.current.value = "";
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const applyPreset = (days) => onChange(addDays(days));

  return (
    <div className="space-y-2">
      <div className="flex items-stretch gap-2">
        <Input
          data-testid={testid || "expiry-input"}
          type="date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`flex-1 ${disabled ? "bg-[#F4F1ED] text-[#8E918C] cursor-not-allowed" : ""}`}
          disabled={disabled}
        />
        <input
          ref={camRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          data-testid="expiry-camera-input"
          onChange={(e) => handleScan(e.target.files?.[0])}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          data-testid="expiry-file-input"
          onChange={(e) => handleScan(e.target.files?.[0])}
        />
        <Button
          data-testid="expiry-scan-btn"
          type="button"
          variant="outline"
          size="icon"
          disabled={scanning || disabled}
          onClick={() => {
            // On mobile browsers capture=environment opens camera; on desktop falls back to file picker
            if (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)) {
              camRef.current?.click();
            } else {
              fileRef.current?.click();
            }
          }}
          title="Scansiona scadenza con fotocamera"
          className="border-[#C5E1D1] bg-[#E8F3EC] text-[#2B5A3F] hover:bg-[#D3E8DA] flex-shrink-0"
        >
          {scanning ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
        </Button>
      </div>

      {/* Quick presets */}
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => (
          <button
            key={p.days}
            type="button"
            data-testid={`expiry-preset-${p.days}`}
            onClick={() => applyPreset(p.days)}
            disabled={disabled}
            className={`text-[11px] font-medium px-2 py-1 rounded-md bg-white border border-[#EAE7E1] text-[#5C5F5A] transition-all ${
              disabled
                ? "opacity-50 cursor-not-allowed"
                : "hover:border-[#2E4A3B] hover:text-[#2E4A3B]"
            }`}
          >
            {p.label}
          </button>
        ))}
        {hint && hint.suggested_days > 0 && (
          <button
            type="button"
            data-testid="expiry-preset-suggested"
            onClick={() => onChange(hint.suggested_date)}
            disabled={disabled}
            className={`text-[11px] font-medium px-2 py-1 rounded-md bg-[#FDF6E3] border border-[#F2DEB0] text-[#8B6518] transition-all inline-flex items-center gap-1 ${
              disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-[#F8EDC8]"
            }`}
            title={
              hint.sample_size > 0
                ? `Media ${hint.sample_size} prodott${hint.sample_size === 1 ? "o" : "i"} della cronologia`
                : `Default per ${hint.category}`
            }
          >
            {hint.sample_size > 0 ? <TrendingUp size={10} /> : <Sparkles size={10} />}
            {hint.sample_size > 0 ? `Cronologia ${hint.suggested_days}g` : `Tipico ${hint.suggested_days}g`}
          </button>
        )}
      </div>
    </div>
  );
}
